import {
  defaultRatesSchema,
  rateCardEntrySchema,
  staffInviteListSchema,
  staffInviteSchema,
  staffListSchema,
  staffMemberSchema,
  staffRatesSchema,
  staffSeatsSchema,
  type DefaultRates,
  type InviteStaffInput,
  type RateCardEntry,
  type StaffInvite,
  type StaffList,
  type StaffMember,
  type StaffRates,
  type StaffSeats,
  type SetDefaultRateInput,
  type SetStaffRateInput,
  type UpdateStaffInput,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every staff and rate-card call the app makes.
 *
 * Note what is absent: a "delete staff member" and an "edit rate". Neither exists on the API,
 * because a person who leaves is deactivated (their history stays attached to them) and a rate
 * change is a new dated entry rather than an overwrite. Offering either here would be offering
 * a button the server would refuse.
 */

const STAFF = '/staff';

export async function listStaff(client: ApiClient): Promise<StaffList> {
  return await client.get(STAFF, { schema: staffListSchema });
}

export async function getStaffMember(client: ApiClient, userId: string): Promise<StaffMember> {
  return await client.get(`${STAFF}/${userId}`, { schema: staffMemberSchema });
}

export async function getStaffSeats(client: ApiClient): Promise<StaffSeats> {
  return await client.get(`${STAFF}/seats`, { schema: staffSeatsSchema });
}

export async function updateStaffMember(
  client: ApiClient,
  userId: string,
  patch: UpdateStaffInput,
): Promise<StaffMember> {
  return await client.patch(`${STAFF}/${userId}`, patch, { schema: staffMemberSchema });
}

export async function setStaffActive(
  client: ApiClient,
  userId: string,
  active: boolean,
): Promise<StaffMember> {
  return await client.post(
    `${STAFF}/${userId}/${active ? 'reactivate' : 'deactivate'}`,
    {},
    { schema: staffMemberSchema },
  );
}

// --- Invitations ---------------------------------------------------------------------------

export async function listInvites(client: ApiClient): Promise<StaffInvite[]> {
  const page = await client.get(`${STAFF}/invites`, { schema: staffInviteListSchema });
  return page.items;
}

export async function inviteStaff(
  client: ApiClient,
  input: InviteStaffInput,
): Promise<StaffInvite> {
  return await client.post(`${STAFF}/invites`, input, { schema: staffInviteSchema });
}

export async function revokeInvite(client: ApiClient, inviteId: string): Promise<void> {
  await client.del(`${STAFF}/invites/${inviteId}`);
}

// --- Rates ---------------------------------------------------------------------------------

/** `at` resolves the grid as it stood on a date — how a past settlement is checked. */
export interface RateParams {
  at?: string;
}

export async function getDefaultRates(
  client: ApiClient,
  params: RateParams = {},
): Promise<DefaultRates> {
  return await client.get(`${STAFF}/rates/defaults`, {
    schema: defaultRatesSchema,
    ...(params.at ? { query: { at: params.at } } : {}),
  });
}

export async function setDefaultRate(
  client: ApiClient,
  input: SetDefaultRateInput,
): Promise<RateCardEntry> {
  return await client.put(`${STAFF}/rates/defaults`, input, { schema: rateCardEntrySchema });
}

export async function getStaffRates(
  client: ApiClient,
  userId: string,
  params: RateParams = {},
): Promise<StaffRates> {
  return await client.get(`${STAFF}/${userId}/rates`, {
    schema: staffRatesSchema,
    ...(params.at ? { query: { at: params.at } } : {}),
  });
}

export async function setStaffRate(
  client: ApiClient,
  userId: string,
  input: SetStaffRateInput,
): Promise<RateCardEntry> {
  return await client.put(`${STAFF}/${userId}/rates`, input, { schema: rateCardEntrySchema });
}

const historySchema = z.object({ items: z.array(rateCardEntrySchema) });

/** The dated trail for one combination — what a settlement argument is read against. */
export async function getRateHistory(
  client: ApiClient,
  options: { userId?: string; garmentTypeKey: string; stage: string },
): Promise<RateCardEntry[]> {
  const path = options.userId
    ? `${STAFF}/${options.userId}/rates/history`
    : `${STAFF}/rates/defaults/history`;
  const page = await client.get(path, {
    schema: historySchema,
    query: { garmentTypeKey: options.garmentTypeKey, stage: options.stage },
  });
  return page.items;
}
